import { createRoute, useNavigate } from '@tanstack/react-router';
import { useState, useEffect, type FormEvent } from 'react';
import { Globe } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { usePreferences, applyTheme, setThemeOverride } from '../hooks/usePreferences';
import { useTranslation } from '../i18n';
import type { Locale } from '../i18n';
import { Input } from '../components/ui/Input';
import { Button } from '../components/ui/Button';
import { Route as rootRoute } from './__root';
import styles from './login.module.css';

export const Route = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  component: LoginPage,
});

function LoginPage() {
  const navigate = useNavigate();
  const { login, register, isAuthenticated } = useAuth();
  const { preferences, setPreference } = usePreferences();
  const { t, locale } = useTranslation();
  const [mode, setMode] = useState<'login' | 'register'>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [error, setError] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('error') === 'GoogleAuthFailed' ? 'Google authentication failed' : '';
  });
  const [isLoading, setIsLoading] = useState(false);

  // Force Vivid theme on auth pages, restore user's theme on leave
  useEffect(() => {
    setThemeOverride(true);
    applyTheme('vivid');
    return () => {
      setThemeOverride(false);
      applyTheme(preferences.theme);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Redirect if already authenticated
  if (isAuthenticated) {
    navigate({ to: '/dashboard' });
    return null;
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      if (mode === 'login') {
        await login(email, password);
      } else {
        await register({ email, password, displayName });
      }

      // Referral redirect: if user came from a catalog link, send them back
      const params = new URLSearchParams(window.location.search);
      const ref = params.get('ref');
      const from = params.get('from');
      if (ref === 'catalog' && from) {
        navigate({ to: '/profile/$slug', params: { slug: from } });
      } else {
        navigate({ to: '/dashboard' });
      }
    } catch (err: unknown) {
      const message =
        err && typeof err === 'object' && 'message' in err
          ? String(err.message)
          : t('login.somethingWentWrong');
      setError(message);
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <div className={styles.page}>
      <div className={styles.decorPanel}>
        <img src="/icon.svg" alt="SiliconVault" className={styles.decorLogo} />
        <div className={styles.decorTitle}>SiliconVault</div>
        <div className={styles.decorTagline}>{t('login.subtitle')}</div>
      </div>
      <div className={styles.formColumn}>
      <div className={styles.controls}>
        <div className={styles.langPill} role="group" aria-label="Language">
          <Globe size={13} strokeWidth={1.75} className={styles.langIcon} />
          {(['en', 'he'] as Locale[]).map((loc) => (
            <button
              key={loc}
              className={`${styles.langOption} ${locale === loc ? styles.langOptionActive : ''}`}
              onClick={() => setPreference('locale', loc)}
              aria-pressed={locale === loc}
            >
              {loc.toUpperCase()}
            </button>
          ))}
        </div>
      </div>
      <div className={styles.container}>
        <img src="/icon.svg" alt="SiliconVault" className={styles.brandIcon} />
        <h1 className={styles.title}>SiliconVault</h1>
        <p className={styles.subtitle}>{t('login.subtitle')}</p>

        <div className={styles.card}>
          <div className={styles.tabs}>
            <button
              className={`${styles.tab} ${mode === 'login' ? styles.tabActive : ''}`}
              onClick={() => setMode('login')}
            >
              {t('login.tabLogin')}
            </button>
            <button
              className={`${styles.tab} ${mode === 'register' ? styles.tabActive : ''}`}
              onClick={() => setMode('register')}
            >
              {t('login.tabRegister')}
            </button>
          </div>

          <form className={styles.form} onSubmit={handleSubmit}>
            {mode === 'register' && (
              <Input
                label={t('login.displayName')}
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder={t('login.placeholderName')}
                required
              />
            )}
            <Input
              label={t('login.email')}
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t('login.placeholderEmail')}
              required
            />
            <Input
              label={t('login.password')}
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={t('login.placeholderPassword')}
              required
              minLength={8}
            />

            {error && <div className={styles.error}>{error}</div>}

            <Button type="submit" fullWidth disabled={isLoading}>
              {isLoading
                ? t('login.loading')
                : mode === 'login'
                  ? t('login.tabLogin')
                  : t('login.createAccount')}
            </Button>

            <div className={styles.divider}>
              <span>or</span>
            </div>

            <Button
              type="button"
              variant="secondary"
              fullWidth
              onClick={() => {
                window.location.href = '/api/auth/google';
              }}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ marginRight: '8px' }}>
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
              </svg>
              Continue with Google
            </Button>
          </form>
        </div>
      </div>
      </div>
    </div>
  );
}
